import { useState } from 'react';
import { Box, Button, Group, SegmentedControl, SimpleGrid, Switch } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { DataTableColumn } from 'mantine-datatable';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { BillingDetail } from './hooks/useBilling';
import BillingDetailModal from './BillingDetailModal';
import BillingEditModal from './BillingEditModal';
import { formatPenceToPounds, getBillingTypeLabel } from './billingUtils';
import { SummaryCard } from './SummaryCard';

const columns: DataTableColumn<BillingDetail>[] = [
  { accessor: 'companyName', title: 'Company name', sortable: true },
  {
    accessor: 'billingType',
    title: 'Billing type',
    sortable: true,
    render: row => getBillingTypeLabel(row.billingType),
  },
  {
    accessor: 'billingAmount',
    title: 'Price per unit (£)',
    sortable: true,
    render: row =>
      row.billingAmount !== null && row.billingAmount !== undefined
        ? `£${Number(row.billingAmount).toFixed(2)}`
        : '-',
  },
  {
    accessor: 'billableUnitCount',
    title: 'Total units',
    sortable: true,
  },
  {
    accessor: 'monthlyBillPence',
    title: 'Total bill (£)',
    sortable: true,
    render: row => formatPenceToPounds(row.monthlyBillPence),
  },
  {
    accessor: 'billingStartDate',
    title: 'Start date',
    sortable: true,
    render: row => {
      if (!row.billingStartDate) return '-';
      const d = new Date(row.billingStartDate + 'T00:00:00');
      return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    },
  },
  {
    accessor: 'hasWarnings',
    title: 'Warnings',
    render: row => (row.hasWarnings ? <IconAlertTriangle size={16} color="orange" /> : null),
  },
];

function isActive(row: BillingDetail): boolean {
  if (!row.billingStartDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(row.billingStartDate + 'T00:00:00') <= today;
}

function getEffectiveBill(row: BillingDetail): number {
  const bill = row.billOnlyEngaged ? row.engagedMonthlyBillPence : row.monthlyBillPence;
  return bill ?? 0;
}

function computeSummary(data: BillingDetail[] | undefined, showInternal: boolean) {
  if (!data) return { active: 0, pending: 0, total: 0 };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let active = 0;
  let pending = 0;

  for (const row of data) {
    if (!showInternal && row.isInternal) continue;
    const bill = getEffectiveBill(row);
    if (!bill) continue;

    if (!row.billingStartDate) {
      pending += bill;
      continue;
    }

    const startDate = new Date(row.billingStartDate + 'T00:00:00');
    if (startDate <= today) {
      active += bill;
    } else {
      pending += bill;
    }
  }

  return { active, pending, total: active + pending };
}

export default function BillingTab() {
  const [showInternal, setShowInternal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRow, setSelectedRow] = useState<BillingDetail | null>(null);
  const [editRow, setEditRow] = useState<BillingDetail | null>(null);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);

  const { data: billingData } = useQuery<BillingDetail[]>({
    queryKey: ['admin', 'billing'],
  });

  const summary = computeSummary(billingData, showInternal);

  const handleRowClick = (row: BillingDetail) => {
    setSelectedRow(row);
    openModal();
  };

  const handleModalClose = () => {
    closeModal();
    setSelectedRow(null);
  };

  const handleEditClose = () => {
    closeEdit();
    setEditRow(null);
  };

  const columnsWithEdit: DataTableColumn<BillingDetail>[] = [
    ...columns,
    {
      accessor: 'edit',
      title: 'Actions',
      render: row => (
        <Button
          size="xs"
          variant="light"
          onClick={e => {
            e.stopPropagation();
            setEditRow(row);
            openEdit();
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <BillingDetailModal
        billingDetail={modalOpened ? selectedRow : null}
        onClose={handleModalClose}
      />
      <BillingEditModal billingDetail={editOpened ? editRow : null} onClose={handleEditClose} />

      <SimpleGrid cols={3} mb="md">
        <SummaryCard
          label="Active MRR"
          value={formatPenceToPounds(summary.active)}
          tooltip="Companies whose billing start date has passed"
        />
        <SummaryCard
          label="Pipeline MRR"
          value={formatPenceToPounds(summary.pending)}
          tooltip="Companies not yet billing (future or no start date)"
        />
        <SummaryCard
          label="Projected MRR"
          value={formatPenceToPounds(summary.total)}
          tooltip="Active + Pipeline — total once all companies are billing"
        />
      </SimpleGrid>

      <SimpleTable<BillingDetail>
        resource="admin/billing"
        columns={columnsWithEdit}
        title="Billing"
        showCount
        enableSearch
        pageSize={20}
        onRowClick={handleRowClick}
        filterPredicate={(item, search) => {
          if (!showInternal && item.isInternal) return false;
          if (statusFilter === 'active' && !isActive(item)) return false;
          if (statusFilter === 'pipeline' && isActive(item)) return false;
          if (!search.trim()) return true;
          const s = search.toLowerCase();
          return (
            item.companyName.toLowerCase().includes(s) || item.tenantName.toLowerCase().includes(s)
          );
        }}
        paginationDependencies={[showInternal, statusFilter]}
        titleSuffix={
          <Group gap="md">
            <SegmentedControl
              size="xs"
              value={statusFilter}
              onChange={setStatusFilter}
              data={[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'pipeline', label: 'Pipeline' },
              ]}
            />
            <Switch
              label="Show internal companies"
              checked={showInternal}
              onChange={e => setShowInternal(e.currentTarget.checked)}
            />
          </Group>
        }
      />
    </Box>
  );
}
